import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonGroup } from 'react-bootstrap';
import { buildFactoryOptions } from '../output-performance/outputPerformanceConfig.js';
import { buildOutputPerformanceView } from '../output-performance/outputPerformanceAdapter.js';
import { formatValue } from '../output-performance/outputPerformanceShared';
import { AreaSection } from '../output-performance/AreaCards';
import { OutputDetailModal } from '../output-performance/OutputDetailModal';

function FactoryFilter({ value, onChange, options }) {
  return (
    <ButtonGroup size="sm" className="output-factory-filter">
      {options.map(option => (
        <Button
          key={option.value}
          variant={value === option.value ? 'primary' : 'outline-primary'}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </ButtonGroup>
  );
}

FactoryFilter.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired
};

function MetadataMissingState() {
  return (
    <div className="output-metadata-missing">
      <strong>Metadata not available</strong>
      <span>
        Output Performance now reads structure from backend metadata only.
      </span>
    </div>
  );
}

export default function OutputPerformanceTab({
  date,
  metadataFactories = [],
  outputPerformanceItems = [],
  ancillaryItems = [],
  hardwareItems = [],
  numberOfPiecesItems = [],
  numberOfFrameBoxesItems = [],
  weldedCillItems = [],
  compositeDoorGearingItems = [],
  hardwarePackingItems = [],
  readlyToLoadItems = []
}) {
  const factories = Array.isArray(metadataFactories) ? metadataFactories : [];
  const factoryOptions = useMemo(() => buildFactoryOptions(factories), [factories]);
  const [factoryId, setFactoryId] = useState(factoryOptions[0]?.value || '');
  const [selectedDetail, setSelectedDetail] = useState(null);

  useEffect(() => {
    if (!factoryOptions.some(option => option.value === factoryId)) {
      setFactoryId(factoryOptions[0]?.value || '');
    }
  }, [factoryId, factoryOptions]);

  const view = useMemo(
    () =>
      buildOutputPerformanceView({
        factoryId,
        factories,
        productionItems: outputPerformanceItems,
        ancillaryItems,
        hardwareItems,
        numberOfPiecesItems,
        numberOfFrameBoxesItems,
        weldedCillItems,
        compositeDoorGearingItems,
        readlyToLoadItems
      }),
    [ancillaryItems,readlyToLoadItems, compositeDoorGearingItems, factoryId, factories, hardwareItems, numberOfFrameBoxesItems, numberOfPiecesItems, outputPerformanceItems, weldedCillItems]
  );

  useEffect(() => {
    setSelectedDetail(null);
  }, [factoryId]);

  if (!factories.length) {
    return <MetadataMissingState />;
  }

  return (
    <div className="output-performance-tab">
      <div className="output-performance-header">
        <div>
          <h3>Output Performance</h3>
          <div className="output-performance-subtitle">
            {view.name} factory areas{date ? ` (${date})` : ''}
          </div>
        </div>
        <FactoryFilter
          value={factoryId}
          onChange={setFactoryId}
          options={factoryOptions}
        />
      </div>

      <div className="output-performance-total" data-source={view.source}>
        <span>Total Output</span>
        <strong>{formatValue(view.total)}</strong>
      </div>

      <div className="output-area-sections">
        {view.areas.map(area => (
          <AreaSection
            key={area.name}
            area={area}
            onSelectStation={setSelectedDetail}
            compositeDoorGearingItems={compositeDoorGearingItems}
            hardwarePackingItems={hardwarePackingItems}
          />
        ))}
      </div>

      <OutputDetailModal
        detail={selectedDetail}
        factoryName={view.name}
        onHide={() => setSelectedDetail(null)}
      />
    </div>
  );
}

OutputPerformanceTab.propTypes = {
  date: PropTypes.string,
  metadataFactories: PropTypes.array,
  outputPerformanceItems: PropTypes.arrayOf(
    PropTypes.shape({
      stationPath: PropTypes.string,
      StationPath: PropTypes.string,
      stationName: PropTypes.string,
      StationName: PropTypes.string,
      shift: PropTypes.string,
      Shift: PropTypes.string,
      userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      UserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      outputCount: PropTypes.number,
      OutputCount: PropTypes.number,
      isBox: PropTypes.bool,
      IsBox: PropTypes.bool
    })
  ),
  ancillaryItems: PropTypes.array,
  hardwareItems: PropTypes.array,
  numberOfPiecesItems: PropTypes.array,
  numberOfFrameBoxesItems: PropTypes.array,
  weldedCillItems: PropTypes.array,
  compositeDoorGearingItems: PropTypes.array,
  hardwarePackingItems: PropTypes.array,
  readlyToLoadItems: PropTypes.array
};
